import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Navigate } from 'react-router-dom';
import { Package } from 'lucide-react';

const statusColors: Record<string, string> = {
  pending: 'bg-gold/20 text-foreground',
  confirmed: 'bg-soft-green/30 text-foreground',
  payment_confirmed: 'bg-primary/15 text-primary',
  shipped: 'bg-primary/10 text-primary',
  delivered: 'bg-soft-green/50 text-foreground',
  cancelled: 'bg-destructive/10 text-destructive',
};

const MyOrders = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();

  if (!user) return <Navigate to="/auth" replace />;

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['my-orders', user.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('orders')
        .select('*, artworks(title, title_ar, image_url)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      return data || [];
    },
  });

  const statusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: t('pending'), confirmed: t('confirmed'),
      payment_confirmed: t('paymentConfirmed'),
      shipped: t('shipped'), delivered: t('delivered'),
      cancelled: t('cancelled'),
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen pt-16">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <h1 className="font-display text-xl md:text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
          <Package className="h-5 w-5 text-primary" />
          {t('myOrders')}
        </h1>

        {isLoading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}</div>
        ) : orders.length === 0 ? (
          <p className="text-center text-muted-foreground py-16">{t('noArtworks')}</p>
        ) : (
          <div className="space-y-3">
            {orders.map((order: any) => (
              <div key={order.id} className="flex items-center gap-3 md:gap-4 p-3 md:p-4 glass-panel border-0 rounded-xl">
                {order.artworks?.image_url && (
                  <img src={order.artworks.image_url} alt="" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground text-sm truncate">
                    {lang === 'ar' ? order.artworks?.title_ar || order.artworks?.title : order.artworks?.title || 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('currency')}{order.total_price}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString(lang === 'ar' ? 'ar' : 'en')}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${statusColors[order.status] || ''}`}>
                  {statusLabel(order.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
